/**
 * Weave 2 (`/weave2`): sessions from the cloud session store, in the VS Code extension's own webview.
 *
 * This page is React glue only — it lends the chat a div and the user's credentials. Everything inside the div
 * (the extension's ChatView, composer, permission and question docks and session list, with a browser host in
 * place of VS Code) is the `@ncoder/web-vscode` bundle. It is additive: it shares nothing with `/weave` or
 * the MSA services.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { loadWeave2Chat, weave2MountOptions } from "@/services/weave2/chatBundle";

type Phase = "loading" | "ready" | "missing";

export function Weave2Route() {
  const host = useRef<HTMLDivElement>(null);
  const [params, setParams] = useSearchParams();
  const [phase, setPhase] = useState<Phase>("loading");
  const [detail, setDetail] = useState<string>();
  // Read once: later URL changes come from the chat itself, and must not remount it.
  const initial = useRef(params.get("session") ?? undefined);

  useEffect(() => {
    let mounted: { unmount: () => void } | undefined;
    let cancelled = false;
    loadWeave2Chat()
      .then((chat) => {
        if (cancelled || !host.current) return;
        mounted = chat.mount(
          host.current,
          weave2MountOptions({
            sessionID: initial.current,
            onSessionChange: (id) => setParams(id ? { session: id } : {}, { replace: true }),
          })
        );
        setPhase("ready");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setDetail(e instanceof Error ? e.message : String(e));
        setPhase("missing");
      });
    return () => {
      cancelled = true;
      mounted?.unmount();
    };
    // setParams is stable per router; the chat is mounted once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="weave2-route" data-phase={phase}>
      {phase === "loading" && (
        <div className="p-6 text-sm text-muted-foreground" role="status">
          Loading Weave 2...
        </div>
      )}
      {phase === "missing" && (
        <div className="m-6 rounded-md border p-4 text-sm" role="alert" data-testid="weave2-missing">
          <p className="font-medium">Weave 2 could not be loaded.</p>
          <p className="mt-1 text-muted-foreground">
            The chat bundle is missing or failed to start. Run <code>scripts/sync-weave2-chat.sh</code> to copy a build of
            ncoder-vscode&apos;s <code>packages/web-vscode</code> into <code>public/weave2-chat/</code>, or set{" "}
            <code>NCODER_PUBLIC_WEAVE2_CHAT_URL</code>.
          </p>
          {detail && <p className="mt-1 font-mono text-xs text-muted-foreground">{detail}</p>}
        </div>
      )}
      <div ref={host} className="min-h-0 flex-1" data-testid="weave2-host" hidden={phase !== "ready"} />
    </div>
  );
}

export default Weave2Route;
